// Three.js particle wave hero background.
// Renders thousands of small dots in a 3D plane that ripples sinusoidally over time
// with subtle mouse parallax. Auto-resizes. Designed to live at position:absolute inset:0.
import { useEffect, useRef } from "react";
import * as THREE from "three";

export function ParticleWave({ accent = "#C9A227", density = 1, amplitude = 1, speed = 1 }) {
  const ref = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    // Respect reduced-motion: render a single static frame instead of animating forever.
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const w = () => container.clientWidth;
    const h = () => container.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, w() / h(), 1, 5000);
    camera.position.set(0, 240, 380);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w(), h());
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Grid of particles
    const COLS = Math.round(80 * density);
    const ROWS = Math.round(40 * density);
    const SEP = 16;
    const N = COLS * ROWS;

    const positions = new Float32Array(N * 3);
    const scales = new Float32Array(N);

    let i = 0, j = 0;
    for (let ix = 0; ix < COLS; ix++) {
      for (let iy = 0; iy < ROWS; iy++) {
        positions[i] = ix * SEP - (COLS * SEP) / 2;
        positions[i + 1] = 0;
        positions[i + 2] = iy * SEP - (ROWS * SEP) / 2;
        scales[j] = 1;
        i += 3;
        j++;
      }
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geom.setAttribute("scale", new THREE.BufferAttribute(scales, 1));

    const accentColor = new THREE.Color(accent);
    const baseColor = new THREE.Color("#3a3a3a");

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uAccent: { value: accentColor },
        uBase: { value: baseColor },
      },
      vertexShader: `
        attribute float scale;
        varying float vScale;
        void main() {
          vScale = scale;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = scale * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 uAccent;
        uniform vec3 uBase;
        varying float vScale;
        void main() {
          // round point
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          if (d > 0.5) discard;
          float edge = smoothstep(0.5, 0.35, d);
          // mix base→accent based on vScale (peaks light up amber)
          float t = clamp((vScale - 1.0) / 2.0, 0.0, 1.0);
          vec3 col = mix(uBase, uAccent, t);
          float a = edge * mix(0.35, 1.0, t);
          gl_FragColor = vec4(col, a);
        }
      `,
      transparent: true,
      depthTest: false,
    });

    const points = new THREE.Points(geom, material);
    scene.add(points);

    function onMove(e) {
      const r = container.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
      mouseRef.current.tx = nx;
      mouseRef.current.ty = ny;
    }
    if (!reduceMotion) window.addEventListener("mousemove", onMove);

    function onResize() {
      camera.aspect = w() / h();
      camera.updateProjectionMatrix();
      renderer.setSize(w(), h());
    }
    window.addEventListener("resize", onResize);

    let raf = 0;
    let t0 = performance.now();
    function frame(t) {
      const m = mouseRef.current;
      m.x += (m.tx - m.x) * 0.05;
      m.y += (m.ty - m.y) * 0.05;

      const pos = geom.attributes.position.array;
      const sc = geom.attributes.scale.array;
      let p = 0;
      for (let ix = 0; ix < COLS; ix++) {
        for (let iy = 0; iy < ROWS; iy++) {
          const wave =
            Math.sin((ix + t * speed * 1.6) * 0.18) * 26 +
            Math.cos((iy + t * speed * 1.2) * 0.22) * 22 +
            Math.sin((ix + iy + t * speed * 0.8) * 0.12) * 14;
          pos[p * 3 + 1] = wave * amplitude;
          const peak = (wave + 60) / 120; // 0..1
          sc[p] = 0.6 + peak * 2.6;
          p++;
        }
      }
      geom.attributes.position.needsUpdate = true;
      geom.attributes.scale.needsUpdate = true;

      camera.position.x = m.x * 60;
      camera.position.y = 240 - m.y * 40;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
    }

    if (reduceMotion) {
      frame(0);
    } else {
      const tick = () => {
        const t = (performance.now() - t0) / 1000;
        frame(t);
        raf = requestAnimationFrame(tick);
      };
      tick();
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      try {
        renderer.dispose();
        geom.dispose();
        material.dispose();
        if (renderer.domElement && renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
      } catch {
        // container may already be unmounted
      }
    };
  }, [accent, density, amplitude, speed]);

  return <div ref={ref} style={{ position: "absolute", inset: 0, pointerEvents: "none" }} />;
}
